import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "jiffy-api";
import { objNamesToSnakeCase, objNamesToCamelCase } from "common/utils/helpers/cases";
import type { UpdateDesignProjectRequest } from "@design-lab/shared/types";

export function useUpdateDesignProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, payload }: UpdateDesignProjectRequest) => {
      const snakeCasePayload = objNamesToSnakeCase(payload);
      try {
        const response = await api.designLab.updateDesignProject({ id }, snakeCasePayload);
        return objNamesToCamelCase(response);
      } catch (e: any) {
        const errorResponse = e?.response;

        // If project not found with redirect instruction, redirect silently (without Honeybadger)
        if (errorResponse?.error === "project_not_found" && errorResponse?.redirect) {
          window.location.href = errorResponse.redirect;
          return new Promise(() => {});
        }

        window.Honeybadger?.notify("Failed to update design project", {
          params: {
            error: e instanceof Error ? e.message : String(e),
            error_object: e instanceof Error ? e.toString() : String(e),
            stack_trace: e instanceof Error ? e.stack : null,
            hook: "useUpdateDesignProject",
            id,
            payload
          }
        });
        const [firstMessage] = errorResponse?.messages ?? ["An unknown error occurred"];
        const error = new Error(firstMessage);
        throw error;
      }
    },
    onSuccess: data => {
      // Update the cache with the new data from the PUT response
      // This prevents any GET requests as long as the response is complete
      queryClient.setQueryData(["designProject"], data);
    }
  });
}
